import { useCallback, useEffect, useState } from "react";
import Papa from "papaparse";
import {
  Box,
  InputAdornment,
  TableCell,
  TableRow,
  TextField,
  Typography,
  Card,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";

import TableWrapper from "components/TableWrapper";
import Loader from "components/Loader";
import ButtonV1 from "components/ButtonV1";
import StatusChip from "components/StatusChip";
import useToaster from "hooks/useToaster";
import {
  fetchReservationLedgerData,
  getReservationCellValue,
  fetchAvailabilityData,
} from "services/availabilityService";
import { downloadCsv } from "utils";
import { RESERVATION_LEDGER_COLUMNS } from "constants/availabilityConfig";
import ReservationFilters from "../ReservationFilters";

const ReservationLedger = () => {
  const theme = useTheme();
  const triggerToaster = useToaster();

  const [filters, setFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [filterErrors, setFilterErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [summaryData, setSummaryData] = useState({
    netAvailable: null,
    reserved: null,
    isLoading: false,
    isVisible: false,
    type: null,
  });

  useEffect(() => {
    const fetchSummary = async () => {
      const hasMmid = Boolean(appliedFilters.mm_id);
      const hasVendor = appliedFilters.vendor_ids?.length > 0;
      const hasSellers = appliedFilters.seller?.length > 0;
      const hasRefId = Boolean(appliedFilters.reference_id);

      if (hasMmid && hasVendor && hasSellers) {
        setSummaryData((prev) => ({
          ...prev,
          isLoading: true,
          isVisible: true,
          type: "vendor",
        }));
        try {
          const availResult = await fetchAvailabilityData({
            mm_ids: [appliedFilters.mm_id],
            vendor_ids: appliedFilters.vendor_ids,
            page: 0,
            size: 50,
          });

          let netAvailable = 0;
          if (availResult.rows && availResult.rows.length > 0) {
            netAvailable = availResult.rows.reduce(
              (sum, r) =>
                sum +
                Number(
                  (r?.available_qty ??
                    r?.availability_breakup?.available_qty) ||
                    0,
                ),
              0,
            );
          }

          let reservedQty = 0;
          rows.forEach((r) => {
            const qty = Number(r.qty || 0);
            const eventType = String(r.event_type || "").toUpperCase();
            const isReserve = eventType === "RESERVE";
            const isUnreserve =
              eventType === "UNRESERVE" || eventType === "CLOSED";
            if (isReserve) reservedQty += qty;
            if (isUnreserve) reservedQty -= qty;
          });

          setSummaryData({
            netAvailable,
            reserved: reservedQty,
            isLoading: false,
            isVisible: true,
            type: hasVendor ? "vendor" : "ref",
          });
        } catch {
          setSummaryData((prev) => ({ ...prev, isLoading: false }));
        }
      } else if (hasMmid && hasRefId) {
        let reservedQty = 0;
        rows.forEach((r) => {
          const qty = Number(r.qty || 0);
          const eventType = String(r.event_type || "").toUpperCase();
          const isReserve = eventType === "RESERVE";
          const isUnreserve =
            eventType === "UNRESERVE" || eventType === "CLOSED";
          if (isReserve) reservedQty += qty;
          if (isUnreserve) reservedQty -= qty;
        });
        setSummaryData({
          netAvailable: null,
          reserved: reservedQty,
          isLoading: false,
          isVisible: true,
          type: "ref",
        });
      } else {
        setSummaryData({
          netAvailable: null,
          reserved: null,
          isLoading: false,
          isVisible: false,
          type: null,
        });
      }
    };

    if (!isLoading) {
      void fetchSummary();
    }
  }, [appliedFilters, rows, isLoading]);

  const loadData = useCallback(async (filterParams, pagination) => {
    setIsLoading(true);
    const result = await fetchReservationLedgerData(filterParams, pagination);
    if (result.error) {
      triggerToaster(result.error, "error");
      setRows([]);
    } else {
      setRows(result.rows);
      setTotalRows(result.metadata?.total_elements ?? 0);
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadData(appliedFilters, { page, size: rowsPerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, page, rowsPerPage]);

  const handleFilterChange = ({ name }, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    if (filterErrors[name])
      setFilterErrors((prev) => {
        const e = { ...prev };
        delete e[name];
        return e;
      });
  };

  const handleFilterBlur = ({ name }, value) => {
    if (name === "mm_id" && value) {
      const MMID_REGEX = /^\d{4}-\d{5}-\d{8}$/;
      if (!MMID_REGEX.test(value)) {
        setFilterErrors((prev) => ({ ...prev, [name]: "Invalid MMid format" }));
      }
    }
  };

  const handleApplyFilters = useCallback((payload) => {
    setAppliedFilters(payload);
    setPage(0);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters({});
    setAppliedFilters({});
    setFilterErrors({});
    setPage(0);
  }, []);

  const handleExport = async () => {
    setIsExporting(true);
    const result = await fetchReservationLedgerData(appliedFilters, {
      page: 0,
      size: -1,
    });
    if (result.error) {
      triggerToaster(result.error, "error");
    } else if (result.rows.length) {
      const exportData = result.rows.map((row) => {
        const exportRow = {};
        RESERVATION_LEDGER_COLUMNS.forEach((col) => {
          exportRow[col.headerName] = getReservationCellValue(col.field, row);
        });
        return exportRow;
      });
      const csv = Papa.unparse(exportData);
      downloadCsv(csv, "Reservation_Ledger");
    } else {
      triggerToaster("No data available to export", "info");
    }
    setIsExporting(false);
  };

  // ─── Cell renderer ───────────────────────────────────────────────────────────

  const renderCell = (col, row) => {
    const value = getReservationCellValue(col.field, row);

    if (col.field === "event_type") {
      const isReserve = String(value).toUpperCase() === "RESERVE";
      return (
        <StatusChip
          label={value}
          type={isReserve ? "SUCCESS" : "ATTENTION"}
          style={{ fontSize: "0.7rem", fontWeight: 700, borderRadius: "4px" }}
        />
      );
    }

    if (col.field === "status") {
      const statusValue = String(value || "").toUpperCase();
      let chipType = "DEFAULT";
      if (statusValue === "ACTIVE") chipType = "SUCCESS";
      else if (statusValue === "CLOSED") chipType = "DEFAULT";

      return (
        <StatusChip
          label={value || "-"}
          type={chipType}
          style={{ fontSize: "0.7rem", fontWeight: 700, borderRadius: "4px" }}
        />
      );
    }
    if (["event_id", "reference_id", "mm_id"].includes(col.field)) {
      return (
        <Box sx={{ color: theme.palette.accent.main, fontWeight: 500 }}>
          {value}
        </Box>
      );
    }

    if (col.field === "qty") {
      return <Box sx={{ fontWeight: 700, fontSize: "0.875rem" }}>{value}</Box>;
    }

    return <>{value}</>;
  };

  // ─── Table rendering ─────────────────────────────────────────────────────────

  const filteredRows = searchTerm
    ? rows.filter((row) =>
        RESERVATION_LEDGER_COLUMNS.some((col) =>
          String(getReservationCellValue(col.field, row) ?? "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
        ),
      )
    : rows;

  const tableHeader = RESERVATION_LEDGER_COLUMNS.map((col) => (
    <TableCell
      key={col.field}
      sx={{
        fontWeight: 700,
        whiteSpace: "nowrap",
        backgroundColor: theme.palette.bgColor,
        top: 113,
        zIndex: 9,
      }}
    >
      {col.headerName}
    </TableCell>
  ));

  const tableBody = isLoading
    ? [
        <TableRow key="loading">
          <TableCell
            colSpan={RESERVATION_LEDGER_COLUMNS.length}
            align="center"
            sx={{ py: 4 }}
          >
            <Loader size={28} sx={{ marginTop: 0 }} />
          </TableCell>
        </TableRow>,
      ]
    : filteredRows.length === 0
    ? [
        <TableRow key="empty">
          <TableCell
            colSpan={RESERVATION_LEDGER_COLUMNS.length}
            align="center"
            sx={{ py: 4 }}
          >
            <Typography color="textSecondary">
              {searchTerm ? "No matching records found" : "No data found"}
            </Typography>
          </TableCell>
        </TableRow>,
      ]
    : filteredRows.map((row, idx) => (
        <TableRow key={row._id ?? idx} hover>
          {RESERVATION_LEDGER_COLUMNS.map((col) => (
            <TableCell key={col.field} sx={{ fontSize: "0.85rem", py: 1 }}>
              {renderCell(col, row)}
            </TableCell>
          ))}
        </TableRow>
      ));

  return (
    <Box sx={{ py: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: theme.palette.accent.main }}
          >
            Inventory Reservation Ledger
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Audit trail of all RESERVE / UNRESERVE transactions
          </Typography>
        </Box>
        <ButtonV1
          title={isExporting ? "Exporting..." : "Export XL"}
          onClick={handleExport}
          disabled={isExporting}
          loader={isExporting}
          startIcon={<FileDownloadIcon />}
          variant="outlined"
          size="small"
          style={{
            textTransform: "none",
            borderColor: theme.palette.accent.main,
            color: theme.palette.accent.main,
            fontWeight: 700,
          }}
        />
      </Box>

      <ReservationFilters
        filters={filters}
        errors={filterErrors}
        onFilterChange={handleFilterChange}
        onFilterBlur={handleFilterBlur}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          mx: 1,
        }}
      >
        {summaryData.isVisible && rows.length > 0 ? (
          <Card
            sx={{
              p: 1.25,
              boxShadow: "0px 2px 10px rgba(0,0,0,0.05)",
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: theme.palette.grey.A700,
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "1rem",
              width: "fit-content",
              minWidth: "fit-content",
            }}
          >
            {summaryData.type === "vendor" && (
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                    mb: 0.25,
                    fontSize: "0.65rem",
                    textTransform: "none",
                  }}
                >
                  Net Available Qty
                </Typography>
                {summaryData.isLoading ? (
                  <Loader size={16} sx={{ mt: 0 }} />
                ) : (
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.info.main,
                      lineHeight: 1.2,
                      fontSize: "1rem",
                    }}
                  >
                    {summaryData.netAvailable}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.disabled,
                    mt: 0.25,
                    fontSize: "0.6rem",
                  }}
                >
                  Derived from Availability Dashboard API
                </Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                  mb: 0.25,
                  fontSize: "0.65rem",
                  textTransform: "none",
                }}
              >
                Reserved Qty
              </Typography>
              {summaryData.isLoading ? (
                <Loader size={16} sx={{ mt: 0 }} />
              ) : (
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.info.main,
                    lineHeight: 1.2,
                    fontSize: "1rem",
                  }}
                >
                  {summaryData.reserved}
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.disabled,
                  mt: 0.25,
                  fontSize: "0.6rem",
                }}
              >
                {summaryData.type === "vendor"
                  ? "Derived from Reservation Ledger (MMid + Vendor)"
                  : "Derived from Reservation Ledger (MMid + Ref ID)"}
              </Typography>
            </Box>
          </Card>
        ) : (
          <Box />
        )}

        <TextField
          size="small"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 250 }}
        />
      </Box>

      <TableWrapper
        tableHeader={tableHeader}
        tableBody={tableBody}
        isPagination
        count={totalRows}
        page={page}
        rowsPerPage={rowsPerPage}
        handleChangePage={(_, newPage) => setPage(newPage)}
        handleChangeRowsPerPage={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50]}
        isSticky
      />
    </Box>
  );
};

export default ReservationLedger;
